import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { RegularModal } from '@/components_deprecated/Modal'
import type { OverrideOrAddonForm } from './types'
import { Form } from '@/components_deprecated/Form'
import {
  Checkbox,
  DatePicker,
  TextArea,
  TimePicker,
} from '@/components_deprecated'
import { SalaryCodeSelect } from '@/components_deprecated/Select/SalaryCodeSelect'
import styled, { useTheme } from 'styled-components'
import { addMonths, formatISO, set, subMonths } from 'date-fns'
import { useQuery } from '@apollo/client'
import { ALL_TIME_ADDONS } from './queries'
import { getNodes } from '@/utils/graphql'
import { useState } from 'react'
import { isMobile } from 'react-device-detect'

interface FieldProps {
  alignLeft?: boolean
  alignRight?: boolean
  area?: string
}
export const Field = styled.div<FieldProps>`
  grid-area: ${props => props.area};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${props => props.alignLeft && 'align-items: flex-start;'}
  ${props => props.alignRight && 'align-items: flex-end;'}

  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

const StyledForm = styled(Form)`
  gap: 0.5rem;
`

const DateTimeField = styled(Field)`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 0.5rem;

  label {
    grid-row: 1;
    grid-column: 1 / 3;
    margin: 0 0 0 0;
  }
  ${props => props.theme.media.mobile} {
    .--time-picker-inner-wrapper {
      width: 80px;
      padding: 0;
    }
  }
`

interface EditAddonPromptProps {
  initialData?: OverrideOrAddonForm
  isOverride?: boolean
  isConfiguration?: boolean
  userId?: string
  addonId?: string
  onSubmit: (data: OverrideOrAddonForm | null) => void
}

export const EditAddonPrompt: React.FC<EditAddonPromptProps> = ({
  initialData,
  isOverride,

  isConfiguration = false,

  userId,
  addonId,

  onSubmit,
}) => {
  const theme = useTheme()
  const [initialValidation, setInitialValidation] = useState(false)
  const { data } = useQuery(ALL_TIME_ADDONS, {
    variables: {
      user: userId,
      dateStart: initialData?.datetimeStart
        ? formatISO(initialData?.datetimeStart)
        : formatISO(subMonths(new Date(), 1)),
      dateEnd: initialData?.datetimeEnd
        ? formatISO(initialData?.datetimeEnd)
        : formatISO(addMonths(new Date(), 1)),
    },
    skip:
      !userId ||
      !initialData?.datetimeStart ||
      !initialData?.datetimeEnd ||
      isConfiguration,
    onCompleted: () => {},
  })

  const existingAddons = getNodes(data?.allTimeAddons)
    .filter(addon => addon.id !== addonId)
    .map(addon => ({
      salaryCode: addon.salaryCode?.externalId,
      datetimeStart: new Date(addon.datetimeStart),
      datetimeEnd: new Date(addon.datetimeEnd),
    }))

  const {
    formValues,
    formChangeHandler,
    formValid,
    updateForm,
    formErrors,
    validateForm,
  } = useForm<OverrideOrAddonForm>({
    values: initialData ?? {
      datetimeStart: new Date(),
      datetimeEnd: new Date(),
      salaryCode: '',
      overrideOtherAddons: false,
      salaryCodeIfOvertime: null,
      comment: '',
    },
    config: {
      initAsInvalid: true,
    },
    validators: {
      datetimeStart: (value, other) => {
        if (!value || !other.datetimeEnd) {
          return null
        }
        return value < other.datetimeEnd
          ? isOverride ||
            validateAgainstExistingAddons(value, other.datetimeEnd)
            ? null
            : translations.error.overlap
          : translations.error.startBeforeEnd
      },
      datetimeEnd: (value, other) => {
        if (!value || !other.datetimeStart) {
          return null
        }
        return value > other.datetimeStart
          ? isOverride ||
            validateAgainstExistingAddons(other.datetimeStart, value)
            ? null
            : translations.error.overlap
          : translations.error.startBeforeEnd
      },
      salaryCode: value => {
        return value ? null : translations.error.required
      },
    },
  })

  function validateAgainstExistingAddons(start: Date, end: Date) {
    if (isConfiguration) {
      return true
    }
    for (const addon of existingAddons) {
      if (
        (start >= addon.datetimeStart && start < addon.datetimeEnd) ||
        (end > addon.datetimeStart && end <= addon.datetimeEnd) ||
        (start <= addon.datetimeStart && end >= addon.datetimeEnd)
      ) {
        return false
      }
    }
    return true
  }

  const translations = useTranslate({
    label: {
      datetimeStart: 'common.start',
      datetimeEnd: 'common.end',
      salaryCode: 'common.salary-code',
      salaryCodeIfOvertime: 'common.salary-code-if-overtime',
      overrideOtherAddons: 'salary.override-other-addons',
      comment: 'common.comment',
    },
    create: 'common.create-alt',
    update: 'common.update',

    error: {
      startBeforeEnd: 'timesheets.error.start-before-end',
      required: 'common.required',
      overlap: 'salary.error.addon-overlap',
    },
  })

  function updateTime(key: 'datetimeStart' | 'datetimeEnd') {
    return (value: Date) => {
      const existingValue = formValues[key]
      if (!existingValue) {
        return
      }
      const newValue = set(existingValue, {
        hours: value.getHours(),
        minutes: value.getMinutes(),
      })

      updateForm({
        [key]: newValue,
      })
    }
  }

  function updateDate(key: 'datetimeStart' | 'datetimeEnd') {
    return (value: Date) => {
      const existingValue = formValues[key]
      if (!existingValue) {
        return
      }
      const newValue = set(existingValue, {
        year: value.getFullYear(),
        month: value.getMonth(),
        date: value.getDate(),
      })

      updateForm({
        [key]: newValue,
      })
    }
  }

  if (existingAddons.length > 0 && !initialValidation) {
    validateForm()
    setInitialValidation(true)
  }

  return (
    <RegularModal
      title={translations.create}
      submitText={translations.update}
      submitDisabled={!formValid}
      onCancel={() => onSubmit(null)}
      onSubmit={() => onSubmit(formValues)}
      width={isMobile ? '100%' : '500px'}
    >
      <StyledForm onSubmit={() => onSubmit(formValues)}>
        {formValues.datetimeStart && (
          <DateTimeField>
            <label>{translations.label.datetimeStart}</label>
            <TimePicker
              value={formValues.datetimeStart}
              width="100px"
              onChange={updateTime('datetimeStart')}
              grayed
            />

            <DatePicker
              value={formValues.datetimeStart}
              onChange={updateDate('datetimeStart')}
              grayed
            />
            {formErrors.datetimeStart && (
              <span
                className="text-red absolute top-0 right-0"
                style={{ color: theme.colors.red }}
              >
                {formErrors.datetimeStart}
              </span>
            )}
          </DateTimeField>
        )}
        {formValues.datetimeEnd && (
          <DateTimeField>
            <label>{translations.label.datetimeEnd}</label>
            <TimePicker
              value={formValues.datetimeEnd}
              width="100px"
              onChange={updateTime('datetimeEnd')}
              grayed
            />
            <DatePicker
              value={formValues.datetimeEnd}
              onChange={updateDate('datetimeEnd')}
              grayed
            />
            {formErrors.datetimeEnd && (
              <span
                className="red absolute top-0 right-0"
                style={{ color: theme.colors.red }}
              >
                {formErrors.datetimeEnd}
              </span>
            )}
          </DateTimeField>
        )}
        <Field>
          <label>{translations.label.salaryCode}</label>
          <SalaryCodeSelect
            value={formValues.salaryCode}
            onChange={value =>
              value ? formChangeHandler('salaryCode')(value) : null
            }
            isAddon={!isConfiguration && !!userId}
            grayed
            userId={userId}
          />
        </Field>
        <Field>
          <label>{translations.label.salaryCodeIfOvertime}</label>
          <SalaryCodeSelect
            value={formValues.salaryCodeIfOvertime}
            onChange={value =>
              value ? formChangeHandler('salaryCodeIfOvertime')(value) : null
            }
            isAddon={!isConfiguration && !!userId}
            grayed
            userId={userId}
          />
        </Field>
        {!isOverride &&
          typeof formValues.overrideOtherAddons !== 'undefined' && (
            <Field>
              <label>{translations.label.overrideOtherAddons}</label>
              <Checkbox
                checked={formValues.overrideOtherAddons}
                onChange={formChangeHandler('overrideOtherAddons')}
                grayed
              />
            </Field>
          )}
        <Field>
          <label>{translations.label.comment}</label>
          <TextArea
            value={formValues.comment}
            onChange={formChangeHandler('comment')}
            rows={3}
          />
        </Field>
      </StyledForm>
    </RegularModal>
  )
}
